import { useEffect, useRef, useState } from 'react';
import { useTrackingEvents } from './useTrackingEvents';
import { toast } from './use-toast';

interface UseDriverGeolocationProps {
  orderId: string;
  isActive: boolean;
}

export const useDriverGeolocation = ({ orderId, isActive }: UseDriverGeolocationProps) => {
  const [isTracking, setIsTracking] = useState(false);
  const [lastPosition, setLastPosition] = useState<{ lat: number; lng: number } | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const { createTrackingEvent } = useTrackingEvents(orderId);

  useEffect(() => {
    if (!isActive || !orderId) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        setIsTracking(false);
      }
      return;
    }

    if (!navigator.geolocation) {
      toast({
        title: 'GPS non disponible',
        description: 'Votre navigateur ne supporte pas la géolocalisation',
        variant: 'destructive',
      });
      return;
    }

    setIsTracking(true);

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    const handleSuccess = async (position: GeolocationPosition) => {
      const { latitude, longitude } = position.coords;
      const now = Date.now();

      // Update position every 30 seconds minimum
      if (now - lastUpdateRef.current < 30000) {
        return;
      }

      lastUpdateRef.current = now;
      setLastPosition({ lat: latitude, lng: longitude });

      try {
        await createTrackingEvent({
          order_id: orderId,
          event_type: 'in_transit',
          latitude,
          longitude,
          notes: 'Position GPS mise à jour',
        });
      } catch (error) {
        console.error('Error creating tracking event:', error);
      }
    };

    const handleError = (error: GeolocationPositionError) => {
      console.error('Geolocation error:', error);
      
      let message = 'Erreur de géolocalisation';
      if (error.code === error.PERMISSION_DENIED) {
        message = 'Permission de géolocalisation refusée';
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        message = 'Position non disponible';
      } else if (error.code === error.TIMEOUT) {
        message = 'Timeout de géolocalisation';
      }

      toast({
        title: 'Erreur GPS',
        description: message,
        variant: 'destructive',
      });
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      options
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        setIsTracking(false);
      }
    };
  }, [orderId, isActive, createTrackingEvent]);

  return {
    isTracking,
    lastPosition,
  };
};
